
import React from "react";
import Card from "../methods/card/Card";
import Ussd from "../methods/Ussd";
import Wallet from "../methods/Wallet";
import { i18nManager } from "../../i18n";
import GiftCard from "../methods/GiftCard";
import { Schemes, Utils } from "../../utils";
import { Svgs } from "../../constants/Images";
import { ITransaction } from "../../services";
import MobileMoney from "../methods/MobileMoney";
import BankTransfer from "../methods/BankTransfer";
import { Classname, Dropdown } from "@rivtn/noseur";
import loader from "../../assets/images/loading_circle.gif";
import ExpiryTimeKeeper from "../../utils/ExpiryTimeKeeper";
import { PaymentMethod, PaymentMethodAttributes } from "../../constants/PaymentMethod";

export interface CompoundProps {
    language: string;
    businessId: string;
    transaction: ITransaction;
    switchTransactionCountry: (country: string) => void;
    fetchTransaction: (queryOnly?: boolean, onDone?: (result?: ITransaction, error?: any) => void) => void;
    paymentMethodState: [PaymentMethod | undefined, React.Dispatch<React.SetStateAction<PaymentMethod | undefined>>];
}

function Compound(props: CompoundProps) {

    const businessId = props.businessId;
    const transaction = props.transaction;
    const labels = i18nManager.Labels.compound;
    const loadingState = React.useState<boolean>(false);
    const expiryDate = new Date(transaction.expiry_date);
    const [paymentMethod, setPaymentMethod] = props.paymentMethodState;
    const [showDetails, setShowDetails] = React.useState<boolean>(false);
    const [disablePaymentMethodItems, setDisablePaymentMethodItems] = React.useState<boolean>(false);
    const countryAndCurrency = i18nManager.CountriesAndCurrencies.find(l => l.value === transaction.charge_country_code);

    const componentPayload = {
        businessId,
        transaction,
        loadingState,
        paymentSuccessful,
        fetchTransaction: (onDone?: (result?: ITransaction, error?: any) => void) => props.fetchTransaction(true, onDone),
    };

    return (<div className="compound desktop">
        <div className="details-toggle" onClick={() => setShowDetails(!showDetails)}>
            {showDetails ? Svgs.TriangleRight : Svgs.TriangleLeft}
        </div>
        <div className="container">
            {loadingState[0] && (<div className="container-loading">
                <img src={loader} alt={i18nManager.Labels.loading.loader_alt} />
            </div>)}
            {showDetails && (<div className="details">
                <p className="title">{labels.details.title}</p>
                <div>
                    <div className="info">
                        <div className="line">
                            <span>{labels.details.service}</span>
                            <span>{transaction.service_name}</span>
                        </div>
                        <div className="line">
                            <span>{labels.details.tid}</span>
                            <span className="lined-limit" style={{ "--maxTextLine": 1 } as any}>{transaction.reference}</span>
                        </div>
                        <div className="line">
                            <span>{labels.details.description}</span>
                            <span className="lined-limit">{transaction.description}</span>
                        </div>
                        <div className="line">
                            <span>{labels.details.country}</span>
                            <span>{countryAndCurrency?.label}</span>
                        </div>
                        <div className="line">
                            <span>{labels.details.currency}</span>
                            <span>{countryAndCurrency?.currency_name}</span>
                        </div>
                        <div className="line">
                            <span>{labels.details.request_amount}</span>
                            <span>{(transaction.charge_amount/ Utils.AMOUNT_UNIT)} {countryAndCurrency?.currency_sign}</span>
                        </div>
                        <div className="line">
                            <span>{labels.details.tax}</span>
                            <span>{(transaction.tax/ Utils.AMOUNT_UNIT)} {countryAndCurrency?.currency_sign}</span>
                        </div>
                        <div className="line">
                            <span>{labels.details.discount}</span>
                            <span>{(transaction.discount/ Utils.AMOUNT_UNIT)} {countryAndCurrency?.currency_sign}</span>
                        </div>
                        <div className="line">
                            <span>{labels.details.amount_paid}</span>
                            <span>{(transaction.amount_paid/ Utils.AMOUNT_UNIT)} {countryAndCurrency?.currency_sign}</span>
                        </div>
                    </div>
                    <ExpiryTimeKeeper reportExpiry={() => props.fetchTransaction()} expiryDate={expiryDate} />
                    <div className="line">
                        <span>{labels.details.total_amount}</span>
                        <span>{(transaction.amount/ Utils.AMOUNT_UNIT)} {countryAndCurrency?.currency_sign}</span>
                    </div>
                </div>
            </div>)}
            <div className="navigator">
                <div className="header">
                    <span className="title">{i18nManager.Labels.name}</span>
                </div>
                <div className={Classname.build("methods", { "disable": disablePaymentMethodItems })}>
                    {transaction.payment_options.filter((paymentOption, _) => paymentOption.method in PaymentMethodAttributes).map((paymentOption) => {
                        const methodActive = paymentMethod === paymentOption.method;
                        const paymentMethodAttribute = PaymentMethodAttributes[paymentOption.method];

                        const topStylerClass = Classname.build("divider", { "bottom": methodActive });
                        const bottomStylerClass = Classname.build("divider", { "top": methodActive });
                        return (<React.Fragment key={paymentOption.method}>
                            {methodActive && (<div key={`${paymentOption.method}-ts`} className={topStylerClass}>
                                <div />
                            </div>)}
                            <div key={`${paymentOption.method}-el`} onClick={disablePaymentMethodItems ? undefined : () => setPaymentMethod(paymentOption.method)}
                                className={Classname.build("method", { "active": methodActive, "noseur-disabled": disablePaymentMethodItems && !methodActive }, paymentOption.method.toLocaleLowerCase())}>
                                <paymentMethodAttribute.icon />
                                <span>{paymentMethodAttribute.label}</span>
                            </div>
                            {methodActive && (<div key={`${paymentOption.method}-bs`} className={bottomStylerClass}>
                                <div />
                            </div>)}
                        </React.Fragment>);
                    })}
                </div>
            </div>
            <div className="main-panel">
                <div className="txn-info">
                    <div className="cou-curr">
                        <Dropdown borderless scheme={Schemes.RIVTN_PLOUTOUS} options={i18nManager.CountriesAndCurrencies} formControlProps={{ className: "country-dropdown" }}
                            selectedOptionIndex={i18nManager.CountriesAndCurrencies.findIndex(c => c.value === transaction?.charge_country_code)}
                            onSelectOption={(option) => {
                                props.switchTransactionCountry(option.value);
                                return true;
                            }} disabled={!transaction.configuration.can_change_country || !Utils.equalsAny(transaction.status, [100, 105])} />
                        <Dropdown borderless scheme={Schemes.RIVTN_PLOUTOUS} options={i18nManager.Languages}
                            selectedOptionIndex={i18nManager.Languages.findIndex(l => l.value === props.language)}
                            onSelectOption={(option) => {
                                i18nManager.switchLanguage(option.value);
                                return true;
                            }} disabled={!transaction.configuration.can_change_language || !Utils.equalsAny(transaction.status, [100, 105])} />
                    </div>
                    <div className="mail-amount">
                        <span className="email">{Utils.collapseString(transaction.payer_email, 25)}</span>
                        <span className="amount">{labels.details.pay} <span>{countryAndCurrency?.currency_sign} {(transaction.amount/ Utils.AMOUNT_UNIT)}</span></span>
                    </div>
                </div>
                <div className="payment-form">
                    {paymentMethod === PaymentMethod.CARD && (<Card {...componentPayload} />)}
                    {paymentMethod === PaymentMethod.USSD && (<Ussd {...componentPayload} />)}
                    {paymentMethod === PaymentMethod.WALLET && (<Wallet {...componentPayload} />)}
                    {paymentMethod === PaymentMethod.GIFT_CARD && (<GiftCard {...componentPayload} />)}
                    {paymentMethod === PaymentMethod.MOBILE_MONEY && (<MobileMoney {...componentPayload} />)}
                    {paymentMethod === PaymentMethod.BANK_TRANSFER && (<BankTransfer {...componentPayload} />)}
                </div>
            </div>
        </div>
    </div>);

    function paymentSuccessful() {
        setDisablePaymentMethodItems(true);
    }

}

export default Compound;
